/* global $, APP */
import Logger from 'meet-hour-logger';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';

import {
    createScreenSharingIssueEvent,
    sendAnalytics
} from '../../../react/features/analytics';
import { Avatar } from '../../../react/features/base/avatar';
import { i18next } from '../../../react/features/base/i18n';
import { MHParticipantConnectionStatus } from '../../../react/features/base/lib-meet-hour';
import { MEDIA_TYPE, VIDEO_TYPE } from '../../../react/features/base/media';
import { getScreenShareParticipantID } from '../../../react/features/base/mtApi';
import { getParticipantById } from '../../../react/features/base/participants';
import { getTrackByMediaTypeAndParticipant } from '../../../react/features/base/tracks';
import { CHAT_SIZE } from '../../../react/features/chat';
import { updateKnownLargeVideoResolution } from '../../../react/features/large-video/actions';
import { getParticipantsPaneOpen } from '../../../react/features/participants-pane/functions';
import { PresenceLabel } from '../../../react/features/presence-status';
import { shouldDisplayTileView } from '../../../react/features/video-layout';
import UIEvents from '../../../service/UI/UIEvents';
import { createDeferred } from '../../util/helpers';
import AudioLevels from '../audio_levels/AudioLevels';

import { VIDEO_CONTAINER_TYPE, VideoContainer } from './VideoContainer';

const logger = Logger.getLogger(__filename);

const DESKTOP_CONTAINER_TYPE = 'desktop';
const _reactRootsByContainer = new WeakMap();

/**
 * Returns an existing React root for the given container, or creates one.
 *
 * @param {HTMLElement} container - The target root container.
 * @returns {Object}
 */
function _getOrCreateRoot(container) {
    let root = _reactRootsByContainer.get(container);

    if (!root) {
        root = createRoot(container);
        _reactRootsByContainer.set(container, root);
    }

    return root;
}

/**
 * Unmounts and removes a cached root for the given container.
 *
 * @param {HTMLElement} container - The target root container.
 * @returns {void}
 */
function _disposeRoot(container) {
    if (!container) {
        return;
    }

    const root = _reactRootsByContainer.get(container);

    if (root) {
        root.unmount();
        _reactRootsByContainer.delete(container);
    }
}

/**
 * Manager for all Large containers.
 */
export default class LargeVideoManager {
    /**
     * Checks whether given container is a {@link VIDEO_CONTAINER_TYPE}.
     * FIXME currently this is a workaround for the problem where video type is
     * mixed up with container type.
     *
     * @param {string} containerType
     * @returns {boolean}
     */
    static isVideoContainer(containerType) {
        return (
            containerType === VIDEO_CONTAINER_TYPE
            || containerType === DESKTOP_CONTAINER_TYPE
        );
    }

    /**
     *
     */
    constructor(emitter) {
        /**
         * The map of <tt>LargeContainer</tt>s where the key is the video
         * container type.
         *
         * @type {Object.<string, LargeContainer>}
         */
        this.containers = {};
        this.eventEmitter = emitter;

        this.state = VIDEO_CONTAINER_TYPE;

        // FIXME: We are passing resizeContainer as parameter which is calling
        // Container.resize. Probably there's better way to implement this.
        this.videoContainer = new VideoContainer(
            () => this.resizeContainer(VIDEO_CONTAINER_TYPE),
            emitter
        );
        this.addContainer(VIDEO_CONTAINER_TYPE, this.videoContainer);

        // use the same video container to handle desktop tracks
        this.addContainer(DESKTOP_CONTAINER_TYPE, this.videoContainer);

        /**
         * The preferred width passed as an argument to {@link updateContainerSize}.
         *
         * @type {number|undefined}
         */
        this.preferredWidth = undefined;

        /**
         * The preferred height passed as an argument to {@link updateContainerSize}.
         *
         * @type {number|undefined}
         */
        this.preferredHeight = undefined;

        /**
         * The calculated width that will be used for the large video.
         *
         * @type {number}
         */
        this.width = 0;

        /**
         * The calculated height that will be used for the large video.
         *
         * @type {number}
         */
        this.height = 0;

        /**
         * Cache the aspect ratio of the video displayed to detect changes to
         * the aspect ratio on video resize events.
         *
         * @type {number}
         */
        this._videoAspectRatio = 0;

        this.$container = $('#largeVideoContainer');

        this.$container.hover(
            e => this.onHoverIn(e),
            e => this.onHoverOut(e)
        );

        // Bind event handler so it is only bound once for every instance.
        this._onVideoResolutionUpdate
            = this._onVideoResolutionUpdate.bind(this);

        this.videoContainer.addResizeListener(this._onVideoResolutionUpdate);

        this._dominantSpeakerAvatarContainer = document.getElementById(
            'dominantSpeakerAvatarContainer'
        );

        this.avatarRoot = null;
    }

    /**
     * Removes any listeners registered on child components, including
     * React Components.
     *
     * @returns {void}
     */
    destroy() {
        this.videoContainer.removeResizeListener(this._onVideoResolutionUpdate);

        this.removePresenceLabel();

        // ReactDOM.unmountComponentAtNode(this._dominantSpeakerAvatarContainer);

        _disposeRoot(this._dominantSpeakerAvatarContainer);
        this.avatarRoot = null;

        if (this.presenceRoot) {
            this.presenceRoot.unmount();
            this.presenceRoot = null;
        }
    }

    /**
     *
     */
    onHoverIn(e) {
        if (!this.state) {
            return;
        }
        const container = this.getCurrentContainer();

        container.onHoverIn(e);
    }

    /**
     *
     */
    onHoverOut(e) {
        if (!this.state) {
            return;
        }
        const container = this.getCurrentContainer();

        container.onHoverOut(e);
    }

    /**
     *
     */
    get id() {
        const container = this.getCurrentContainer();

        // If a user switch for large video is in progress then provide what
        // will be the end result of the update.
        if (
            this.updateInProcess
            && this.newStreamData
            && this.newStreamData.id !== container.id
        ) {
            return this.newStreamData.id;
        }

        return container.id;
    }

    /**
     *
     */
    scheduleLargeVideoUpdate() {
        if (this.updateInProcess || !this.newStreamData) {
            return;
        }

        this.updateInProcess = true;

        // Include hide()/fadeOut only if we're switching between users
        const container = this.getCurrentContainer();
        const isUserSwitch = container.id !== this.newStreamData.id
            || container.stream?.getSourceName() !== this.newStreamData.stream?.getSourceName();
        const preUpdate = isUserSwitch ? container.hide() : Promise.resolve();

        preUpdate
            .then(() => {
                const { id, stream, videoType, resolve } = this.newStreamData;

                // FIXME this does not really make sense, because the videoType
                // (camera or desktop) is a completely different thing than
                // the video container type (Etherpad, SharedVideo, VideoContainer).
                const isVideoContainer
                    = LargeVideoManager.isVideoContainer(videoType);

                this.newStreamData = null;

                //  logger.info(`hover in ${id}`);
                this.state = videoType;
                const activeContainer = this.getCurrentContainer();

                activeContainer.setStream(id, stream, videoType);

                // change the avatar url on large
                this.updateAvatar();

                const isVideoMuted = !stream || stream.isMuted();
                const state = APP.store.getState();
                const participant = getParticipantById(state, id);
                const connectionStatus = participant?.connectionStatus;
                const isFakeScreenshare = participant?.isFakeParticipant === 'LocalScreenShare' || participant?.isFakeParticipant === 'RemoteScreenShare';
                const getSCParticipantId = isFakeScreenshare ? getScreenShareParticipantID(id) : id;
                const track = getTrackByMediaTypeAndParticipant(
                    state['features/base/tracks'],
                    isFakeScreenshare ? MEDIA_TYPE.SCREENSHARE : MEDIA_TYPE.VIDEO,
                    id
                );

                const isVideoRenderable
                    = track && !(isVideoMuted && (track && track?.muted))
                    && (APP.conference.isLocalId(getSCParticipantId)
                        || connectionStatus
                            === MHParticipantConnectionStatus.ACTIVE || isFakeScreenshare);
                const isAudioOnly = APP.conference.isAudioOnly();
                const showAvatar
                    = isVideoContainer
                    && ((isAudioOnly && videoType !== VIDEO_TYPE.DESKTOP)
                        || !isVideoRenderable);

                let promise;

                if (
                    this.containers[this.state].videoType === 'desktop' && track && !track.muted
                    && window.location === window.parent.location
                ) {
                    if (document.querySelector('.leftwatermark')) {
                        document.querySelector(
                            '.leftwatermark'
                        ).style.zIndex = 1;
                    }
                } else if (window.location === window.parent.location) {
                    if (document.querySelector('.leftwatermark')) {
                        document.querySelector('.leftwatermark').style.zIndex
                            = '';
                    }
                }


                logger.log('screensharing on large video - remote', getSCParticipantId, this.containers[this.state], track, APP.conference.isLocalId(getSCParticipantId));


                // do not show stream if video is muted
                // but we still should show watermark
                // screensharing mirror issue solved --zaki
                if (
                    showAvatar
                    || (this.containers[this.state].videoType === 'desktop'
                        && APP.conference.isLocalId(getSCParticipantId))
                ) {

                    //         console.log('screensharing on large video - hide', getSCParticipantId, this.containers[this.state], track, APP.conference.isLocalId(getSCParticipantId), 'showAvatar',
                    // 'isVideoContainer', isVideoContainer
                    // , isAudioOnly && videoType !== VIDEO_TYPE.DESKTOP
                    // , 'isVideoRenderable', !isVideoRenderable);
                    this.showWatermark(true);

                    // If the intention of this switch is to show the avatar
                    // we need to make sure that the video is hidden
                    promise = activeContainer.hide();

                    if (
                        (!shouldDisplayTileView(state)
                            || participant?.pinned) // In theory the tile view may not be
                        // enabled yet when we auto pin the participant.

                        && participant
                        && !participant.local
                        && !(participant.isFakeParticipant && !isFakeScreenshare)
                    ) {
                        // remote participant only
                        const isScreenSharing = track?.videoType === 'desktop' && !track.muted;

                        if (isScreenSharing) {
                            // send the event
                            sendAnalytics(
                                createScreenSharingIssueEvent({
                                    source: 'large-video',
                                    connectionStatus,
                                    isVideoMuted,
                                    isAudioOnly,
                                    isVideoContainer,
                                    videoType
                                })
                            );
                        }
                    }
                } else if (this.containers[this.state].videoType === 'desktop'
                        && !APP.conference.isLocalId(getSCParticipantId)

                // && track && !(isVideoMuted && (track && track?.muted))
                ) {
                    this.showWatermark(false);
                    promise = activeContainer.show();

                    // Make sure to force video display for screenshare
                    activeContainer.showAvatar(false);

                    // promise = container.show();
                } else {
                    promise = activeContainer.show();
                }

                // show the avatar on large if needed
                activeContainer.showAvatar(showAvatar);

                // Clean up audio level after previous speaker.
                if (showAvatar) {
                    this.updateLargeVideoAudioLevel(0);
                }

                const messageKey
                    = connectionStatus === MHParticipantConnectionStatus.INACTIVE
                        ? 'connection.LOW_BANDWIDTH'
                        : '';

                // Do not show connection status message in the audio only mode,
                // because it's based on the video playback status.
                const overrideAndHide = APP.conference.isAudioOnly();

                this.updateParticipantConnStatusIndication(
                    id,
                    !overrideAndHide && messageKey
                );

                // Change the participant id the presence label is listening to.
                this.updatePresenceLabel(id);

                this.videoContainer.positionRemoteStatusMessages();

                if (
                    this.containers[this.state].videoType === 'desktop' && track && !track.muted
                    && APP.conference.isLocalId(getSCParticipantId)
                ) {
                    activeContainer.showAvatar(true);
                    this.updateParticipantConnStatusIndication(
                        id,
                        'dialog.screenSharingUser'
                    );
                    this.updatePresenceLabel(id);
                    this.videoContainer.positionRemoteStatusMessages();
                }

                // resolve updateLargeVideo promise after everything is done
                promise.then(resolve);

                return promise;
            })
            .then(() => {
                // after everything is done check again if there are any pending
                // new streams.
                this.updateInProcess = false;
                this.eventEmitter.emit(
                    UIEvents.LARGE_VIDEO_ID_CHANGED,
                    this.id
                );
                this.scheduleLargeVideoUpdate();
            });
    }

    /**
     * Shows/hides notification about participant's connectivity issues to be
     * shown on the large video area.
     *
     * @param {string} id - The id of remote participant(MUC nickname).
     * @param {string|null} messageKey - The i18n key of the message which will be
     * displayed on the large video or <tt>null</tt> to hide it.
     *
     * @private
     */
    updateParticipantConnStatusIndication(id, messageKey) {
        if (messageKey) {
            // Get user's display name
            const displayName = APP.conference.getParticipantDisplayName(id);

            this._setRemoteConnectionMessage(messageKey, { displayName });

            // Show it now only if the VideoContainer is on top
            this.showRemoteConnectionMessage(
                LargeVideoManager.isVideoContainer(this.state)
            );
        } else {
            // Hide the message
            this.showRemoteConnectionMessage(false);
        }
    }

    /**
     * Update large video.
     * Switches to large video even if previously other container was visible.
     *
     * @param userID - The userID of the participant associated with the stream.
     * @param {MHTrack?} stream - New stream.
     * @param {string?} videoType - New video type.
     * @returns {Promise}
     */
    updateLargeVideo(userID, stream, videoType) {
        if (this.newStreamData) {
            this.newStreamData.reject();
        }

        this.newStreamData = createDeferred();
        this.newStreamData.id = userID;
        this.newStreamData.stream = stream;
        this.newStreamData.videoType = videoType;

        this.scheduleLargeVideoUpdate();

        return this.newStreamData.promise;
    }


    /**
     * Update container size.
     */
    updateContainerSize(width, height) {
        if (typeof width === 'number') {
            this.preferredWidth = width;
        }
        if (typeof height === 'number') {
            this.preferredHeight = height;
        }

        let widthToUse = this.preferredWidth || window.innerWidth;
        const state = APP.store.getState();
        const { isOpen } = state['features/chat'];
        const isParticipantsPaneOpen = getParticipantsPaneOpen(state);

        if (isParticipantsPaneOpen) {
            widthToUse -= 315;
        }

        if (isOpen && window.innerWidth > 580) {
            /**
             * If chat state is open, we re-compute the container width
             * by subtracting the default width of the chat.
             */
            widthToUse -= CHAT_SIZE;
        }

        this.width = widthToUse;
        this.height = this.preferredHeight || window.innerHeight;
    }

    /**
     * Resize Large container of specified type.
     *
     * @param {string} type - Type of container which should be resized.
     * @param {boolean} [animate=false] - If resize process should be animated.
     */
    resizeContainer(type, animate = false) {
        const container = this.getContainer(type);

        container.resize(this.width, this.height, animate);
    }

    /**
     * Resize all Large containers.
     *
     * @param {boolean} animate - If resize process should be animated.
     */
    resize(animate) {
        // resize all containers
        Object.keys(this.containers).forEach(type =>
            this.resizeContainer(type, animate)
        );
    }

    /**
     * Updates the src of the dominant speaker avatar.
     */
    updateAvatar() {
        if (!this._dominantSpeakerAvatarContainer) {
            return;
        }

        if (!this.avatarRoot) {
            this.avatarRoot = _getOrCreateRoot(this._dominantSpeakerAvatarContainer);
        }

        this.avatarRoot.render(
            <Provider store = { APP.store }>
                <Avatar
                    id = 'dominantSpeakerAvatar'
                    participantId = { this.id } />
            </Provider>
        );
    }

    /**
     * Updates the audio level indicator of the large video.
     *
     * @param lvl - The new audio level to set.
     */
    updateLargeVideoAudioLevel(lvl) {
        AudioLevels.updateLargeVideoAudioLevel('dominantSpeaker', lvl);
    }

    /**
     * Displays a message of the passed in participant id's presence status. The
     * message will not display if the remote connection message is displayed.
     *
     * @param {string} id - The participant ID whose associated user's presence
     * status should be displayed.
     * @returns {void}
     */
    updatePresenceLabel(id) {
        const presenceLabelContainer = document.getElementById('remotePresenceMessage');

        if (!presenceLabelContainer) {
            return;
        }

        if (!this.presenceRoot) {
            this.presenceRoot = _getOrCreateRoot(presenceLabelContainer);
        }

        this.presenceRoot.render(
            <Provider store = { APP.store }>
                <I18nextProvider i18n = { i18next }>
                    <PresenceLabel
                        className = 'presence-label'
                        participantID = { id } />
                </I18nextProvider>
            </Provider>
        );
    }

    /**
     * Removes the messages about the displayed participant's presence status.
     *
     * @returns {void}
     */
    removePresenceLabel() {
        // const presenceLabelContainer = $('#remotePresenceMessage');

        // if (presenceLabelContainer.length) {
        //     ReactDOM.unmountComponentAtNode(presenceLabelContainer.get(0));
        // }

        const presenceLabelContainer = document.getElementById('remotePresenceMessage');

        _disposeRoot(presenceLabelContainer);
        this.presenceRoot = null;
    }

    /**
     * Show or hide watermark.
     *
     * @param {boolean} show
     */
    showWatermark(show) {
        $('.watermark').css('visibility', show ? 'visible' : 'hidden');
    }

    /**
     * Shows hides the "avatar" message which is to be displayed either in
     * the middle of the screen or below the avatar image.
     *
     * @param {boolean|undefined} [show=undefined] - <tt>true</tt> to show
     * the avatar message or <tt>false</tt> to hide it. If not provided then
     * the connection status of the user currently on the large video will be
     * obtained form "APP.conference" and the message will be displayed if
     * the user's connection is either interrupted or inactive.
     */
    showRemoteConnectionMessage(show) {
        let shouldShow = show;

        if (typeof shouldShow !== 'boolean') {
            const participant = getParticipantById(
                APP.store.getState(),
                this.id
            );
            const connStatus = participant?.connectionStatus;

            shouldShow
                = !APP.conference.isLocalId(this.id)
                && (connStatus === MHParticipantConnectionStatus.INTERRUPTED
                    || connStatus === MHParticipantConnectionStatus.INACTIVE);
        }

        if (shouldShow) {
            $('#remoteConnectionMessage').css({ display: 'block' });
        } else {
            $('#remoteConnectionMessage').hide();
        }
    }

    /**
     * Updates the text which describes that the remote user is having
     * connectivity issues.
     *
     * @param {string} msgKey - The translation key which will be used to get
     * the message text.
     * @param {Object} msgOptions - Translation options object.
     *
     * @private
     */
    _setRemoteConnectionMessage(msgKey, msgOptions) {
        if (msgKey) {
            $('#remoteConnectionMessage')
                .attr('data-i18n', msgKey)
                .attr('data-i18n-options', JSON.stringify(msgOptions));
            APP.translation.translateElement(
                $('#remoteConnectionMessage'),
                msgOptions
            );
        }
    }

    /**
     * Add container of specified type.
     *
     * @param {string} type - Container type.
     * @param {LargeContainer} container - Container to add.
     */
    addContainer(type, container) {
        if (this.containers[type]) {
            throw new Error(`container of type ${type} already exist`);
        }

        this.containers[type] = container;
        this.resizeContainer(type);
    }

    /**
     * Get Large container of specified type.
     *
     * @param {string} type - Container type.
     * @returns {LargeContainer}
     */
    getContainer(type) {
        const container = this.containers[type];

        if (!container) {
            throw new Error(`container of type ${type} doesn't exist`);
        }

        return container;
    }

    /** .........
     * Returns {@link LargeContainer} for the current {@link state}
     *
     * @returns {LargeContainer}
     *
     * @throws an <tt>Error</tt> if there is no container for the current
     * {@link state}.
     */
    getCurrentContainer() {
        return this.getContainer(this.state);
    }

    /** ........
     * Returns type of the current {@link LargeContainer}
     *
     * @returns {string}
     */
    getCurrentContainerType() {
        return this.state;
    }

    /**
     * Remove Large container of specified type.
     *
     * @param {string} type - Container type.
     */
    removeContainer(type) {
        if (!this.containers[type]) {
            throw new Error(`container of type ${type} doesn't exist`);
        }

        delete this.containers[type];
    }

    /**
     * Show Large container of specified type.
     * Does nothing if such container is already visible.
     *
     * @param {string} type - Container type.
     * @returns {Promise}
     */
    showContainer(type) {
        if (this.state === type) {
            return Promise.resolve();
        }

        const oldContainer = this.containers[this.state];

        // FIXME when video is being replaced with other content we need to hide
        // companion icons/messages. It would be best if the container would
        // be taking care of it by itself, but that is a bigger refactoring

        if (LargeVideoManager.isVideoContainer(this.state)) {
            this.showWatermark(false);
            this.showRemoteConnectionMessage(false);
        }
        oldContainer.hide();

        this.state = type;
        const container = this.getContainer(type);

        return container.show().then(() => {
            if (LargeVideoManager.isVideoContainer(type)) {
                // FIXME when video appears on top of other content we need to
                // show companion icons/messages. It would be best if
                // the container would be taking care of it by itself, but that
                // is a bigger refactoring
                this.showWatermark(true);

                // "avatar" and "video connection" can not be displayed both
                // at the same time, but the latter is of higher priority and it
                // will hide the avatar one if will be displayed.
                this.showRemoteConnectionMessage(/* fetch the current state */);
            }
        });
    }

    /**
     * Changes the flipX state of the local video.
     *
     * @param val - {boolean} True if flipped.
     */
    onLocalFlipXChange(val) {
        this.videoContainer.setLocalFlipX(val);
    }

    /**
     * Dispatches an action to update the known resolution state of the large video and adjusts container sizes when the
     * resolution changes.
     *
     * @private
     * @returns {void}
     */
    _onVideoResolutionUpdate() {
        const { height, width } = this.videoContainer.getStreamSize();
        const { resolution } = APP.store.getState()['features/large-video'];

        // "480p", "720p" and the rest name the *short* side of the frame, and that is what the quality label
        // buckets against. Reporting the raw videoHeight read the long side for portrait senders - a phone
        // sending 360x640 reported 640 and was labelled SD when the stream it is actually carrying is 360.
        // Landscape is unaffected: there the short side already is the height.
        const streamResolution = width > 0 && height > 0 ? Math.min(width, height) : height;

        if (streamResolution !== resolution) {
            APP.store.dispatch(updateKnownLargeVideoResolution(streamResolution));
        }

        const currentAspectRatio = height === 0 ? 0 : width / height;

        if (this._videoAspectRatio !== currentAspectRatio) {
            this._videoAspectRatio = currentAspectRatio;
            this.resize();
        }
    }
}
